import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import { InfoCircle } from "iconsax-react";
import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { commonConfigs, urls } from "../../../../../config";
import beveatApiStaging from "../../../../api/staging_beveat_api.js";
import beveatApiProduction from "../../../../api/production_beveat_api.js";
import { TRAINING_PROVIDER_SYSCODE, TrainingProviderSyscodeString } from "../../../../constants/training_provider_syscode.jsx";

const errorIcon = (
  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
    <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
  </div>
);

const user = JSON.parse(localStorage.getItem("user"));

export default function UpdateAdministratorModal({ setOpenModal, row, load }) {
  const [isDisabled, setIsDisabled] = useState(false);
  const [open, setOpen] = useState(true);
  const [roleOptions, setRoleOptions] = useState([]);
  const [trainingProviderOptions, setTrainingProviderOptions] = useState([]);
  const [defaultRole, setDefaultRole] = useState(null);
  const [defaultTrainingProvider, setDefaultTrainingProvider] = useState(null);
  const account_id = row.account_id;

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      account_email: row.account_email,
      account_mobile: row.account_mobile,
    },
  });

  let getAllAccessRolesUrl = ``;
  let updateAdministratorUrl = ``;
  let apiHeaders;
  let apiInstance;

  if (process.env.REACT_APP_ENV === "staging") {
    updateAdministratorUrl = urls.baseUrl + commonConfigs.apiUrls.updateAdministrator(account_id);
    getAllAccessRolesUrl = urls.baseUrl + commonConfigs.apiUrls.getAllAccessRoles();
    apiHeaders = commonConfigs.apiHeader;
    apiInstance = beveatApiStaging;
  } else {
    updateAdministratorUrl = urls.prodUrl + commonConfigs.apiUrls.updateAdministrator(account_id);
    getAllAccessRolesUrl = urls.prodUrl + commonConfigs.apiUrls.getAllAccessRoles();
    apiHeaders = commonConfigs.prodApiHeader;
    apiInstance = beveatApiProduction;
  }

  function formatData(data) {
    return {
      account_email: data.account_email,
      account_mobile: parseInt(data.account_mobile),
      role_id: data.role_id.value,
      training_provider_id: data.training_provider_id?.value || null,
      updated_by: user.account_id, 
    };
  }

  const onSubmit = (data) => {
    setIsDisabled(true);

    const payload = formatData(data);
    console.log("Payload:", payload);

    apiInstance
      .put(updateAdministratorUrl, payload, {
        headers: apiHeaders,
      })
      .then((res) => {
        try {
          if (res.data.status !== "success") {
            alert("Error updating administrator");
          } else {
            alert("Administrator Updated Successfully");
          }
        } catch (err) {
          console.log(err);
        } finally {
          setIsDisabled(false);
          load(true);
          setOpenModal(false);
          window.location.reload();
        }
      })
      .catch((err) => {
        alert(`Error: ${err.response?.data?.message || err.message}`);
        setIsDisabled(false);
      });
  };

  useEffect(() => {
    const getSelectData = async () => {
      try {
        // Fetch roles
        const res = await apiInstance.get(getAllAccessRolesUrl, { headers: apiHeaders });
        const roles = res.data.result.data;

        const formattedRoles = roles.map((role) => ({
          value: role.role_id,
          label: role.role_name,
        }));

        setRoleOptions(formattedRoles);

        const currentRole = formattedRoles.find((r) => r.value === row.role_id);
        setDefaultRole(currentRole);

        const trainingProviders = Object.entries(TrainingProviderSyscodeString)
          .map(([id, name]) => ({
            value: parseInt(id),
            label: name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setTrainingProviderOptions(trainingProviders);

        const currentTrainingProvider = trainingProviders.find(
          (tp) => tp.value === row.training_provider_id
        );
        setDefaultTrainingProvider(currentTrainingProvider);

        reset({
          account_email: row.account_email,
          account_mobile: row.account_mobile,
          role_id: currentRole,
          training_provider_id: currentTrainingProvider,
        });
      } catch (err) {
        console.log("Error fetching data:", err);
      }
    };

    getSelectData();
  }, []);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={setOpen}
      >
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay
              className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity"
              onClick={() => setOpenModal(false)}
            />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900 mb-4"
                      style={{ marginBottom: "40px", marginTop: "-30px" }}
                    >
                      Update Administrator
                    </Dialog.Title>

                    {/* Email Input */}
                    <div className="mt-2">
                      <label
                        htmlFor="account_email"
                        className="block text-sm font-medium text-gray-800"
                      >
                        Email
                      </label>
                      <div className="relative rounded-md shadow-sm">
                        <input
                          type="email"
                          name="account_email"
                          id="account_email"
                          className={
                            !errors.account_email
                              ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                              : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                          }
                          placeholder="Enter Email"
                          {...register("account_email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address",
                            },
                          })}
                        />
                        {errors.account_email && errorIcon}
                      </div>
                      {errors.account_email && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.account_email.message}
                        </p>
                      )}
                    </div>

                    {/* Mobile Number Input */}
                    <div className="mt-4">
                      <label
                        htmlFor="account_mobile"
                        className="block text-sm font-medium text-gray-800"
                      >
                        Mobile Number
                      </label>
                      <div className="relative rounded-md shadow-sm">
                        <input
                          type="text"
                          name="account_mobile"
                          id="account_mobile"
                          className={
                            !errors.account_mobile
                              ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                              : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                          }
                          placeholder="Enter Mobile Number"
                          {...register("account_mobile", {
                            required: "Mobile number is required",
                            pattern: {
                              value: /^[0-9]{8}$/,
                              message: "Mobile number must be 8 digits",
                            },
                          })}
                        />
                        {errors.account_mobile && errorIcon}
                      </div>
                      {errors.account_mobile && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.account_mobile.message}
                        </p>
                      )}
                    </div>

                    {/* Role Select */}
                    <div className="mt-4">
                      <label
                        htmlFor="role_id"
                        className="block text-sm font-medium text-gray-800"
                      >
                        Access Role
                      </label>
                      <div className="sm:col-span-6">
                        <Controller
                          name="role_id"
                          control={control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={roleOptions}
                              isSearchable={true}
                              placeholder="Select a role"
                              className="text-sm"
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Training Provider Select */}
                    <div className="mt-4">
                      <label
                        htmlFor="training_provider_id"
                        className="block text-sm font-medium text-gray-800"
                      >
                        Training Provider (Optional)
                      </label>
                      <div className="sm:col-span-6">
                        <Controller
                          name="training_provider_id"
                          control={control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={trainingProviderOptions}
                              isSearchable={true}
                              isClearable={true}
                              placeholder="Select a training provider"
                              className="text-sm"
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    className="mt-3 w-full inline-flex justify-center border-transparent px-4 py-2 border-none text-base font-medium text-yellow-800 hover:text-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                    disabled={isDisabled}
                  >
                    {isDisabled ? "Updating..." : "Submit"}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md px-4 py-2 border-none bg-white text-base font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => setOpenModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}